import { useEffect, useMemo, useState } from "react"
import { JobSiteDailyHoursView } from "../../../components/jobSites/JobSiteDailyHours"
import { SageHoursScope } from "../../../Interfaces"
import { parseHoursScope, reconcileDailyHours } from "../../../utils/jobSiteDailyHours"
import { isISODate } from "../../../utils/isoDate"

const previewHoursDays = ["2026-09-10", "2026-09-12", "2026-09-15"]

const oakAvenue = reconcileDailyHours({
  success: true,
  jobSiteId: 1,
  scope: "day",
  date: "2026-09-12",
  totalHours: 38.5,
  unassignedHours: 0,
  byEmployee: [
    { employeeId: 3, employeeName: "Chris Park", hours: 7.5 },
    { employeeId: 1, employeeName: "Maria Gonzalez", hours: 8 },
    { employeeId: 2, employeeName: "James Torres", hours: 8 },
    { employeeId: 4, employeeName: "Ana Ruiz", hours: 8 },
    { employeeId: 5, employeeName: "Sam Lee", hours: 6 },
    { employeeId: 6, employeeName: "Luis Ortega", hours: 1 },
  ],
  byCostCode: [
    { costCodeId: 3, name: "Labor", hours: 8 },
    { costCodeId: 1, name: "Framing", hours: 18 },
    { costCodeId: 2, name: "Electrical", hours: 12.5 },
  ],
})

const unassigned = reconcileDailyHours({
  success: true,
  jobSiteId: 1,
  scope: "day",
  date: "2026-09-12",
  totalHours: 9,
  unassignedHours: 1,
  byEmployee: [
    { employeeId: 1, employeeName: "Maria Gonzalez", hours: 8 },
    { employeeId: 6, employeeName: "Luis Ortega", hours: 1 },
  ],
  byCostCode: [
    { costCodeId: 1, name: "Framing", hours: 8 },
  ],
})

const projectTotals = reconcileDailyHours({
  success: true,
  jobSiteId: 1,
  scope: "project",
  date: null,
  totalHours: 86,
  unassignedHours: 2,
  byEmployee: [
    { employeeId: 1, employeeName: "Maria Gonzalez", hours: 24 },
    { employeeId: 2, employeeName: "James Torres", hours: 22 },
    { employeeId: 4, employeeName: "Ana Ruiz", hours: 16 },
    { employeeId: 3, employeeName: "Chris Park", hours: 14 },
    { employeeId: 5, employeeName: "Sam Lee", hours: 8 },
    { employeeId: 6, employeeName: "Luis Ortega", hours: 2 },
  ],
  byCostCode: [
    { costCodeId: 1, name: "Framing", hours: 40 },
    { costCodeId: 2, name: "Electrical", hours: 28 },
    { costCodeId: 3, name: "Labor", hours: 16 },
    { costCodeId: null, name: "Unassigned", hours: 2, unassigned: true },
  ],
})

const emptyDay = reconcileDailyHours({
  success: true,
  jobSiteId: 1,
  scope: "day",
  date: "2026-09-12",
  totalHours: 0,
  unassignedHours: 0,
  byEmployee: [],
  byCostCode: [],
})

/**
 * Unauthenticated visual fixture of Paula’s Job Site hours layout.
 * /preview/job-site-daily-hours?product=construction
 * Variants: default, ?variant=unassigned, ?variant=empty, ?variant=project
 */
export default function DailyHoursPreview() {
  const params = new URLSearchParams(window.location.search)
  const variant = params.get("variant")
  const initialScope = parseHoursScope(params.get("scope") || (variant === "project" ? "project" : "day"))
  const [scope, setScope] = useState<SageHoursScope>(initialScope)
  const [date, setDate] = useState("2026-09-12")

  const summary = useMemo(() => {
    if (variant === "empty") return { ...emptyDay, scope }
    if (variant === "unassigned") return unassigned
    if (scope === "project" || variant === "project") return projectTotals
    return oakAvenue
  }, [scope, variant])

  useEffect(() => {
    document.documentElement.classList.add("construction-walk")
  }, [])

  return (
    <div className="min-h-screen p-4 md:p-8 bg-secondary-extra-light">
      <JobSiteDailyHoursView
        siteName="Oak Avenue Remodel"
        date={date}
        scope={scope}
        onScopeChange={setScope}
        onPreviousDay={() => {
          const prev = previewHoursDays.filter((day) => day < date).pop()
          if (prev) setDate(prev)
        }}
        onNextDay={() => {
          const next = previewHoursDays.find((day) => day > date)
          if (next) setDate(next)
        }}
        onDateChange={(next) => {
          if (isISODate(next)) setDate(next)
        }}
        enabledDates={previewHoursDays}
        canGoPrevious={previewHoursDays.some((day) => day < date)}
        canGoNext={previewHoursDays.some((day) => day > date)}
        summary={summary}
      />
    </div>
  )
}
